import {RefObject, useRef, useState, useEffect} from 'react';
import {view, css, Icon, tokens, Button} from '@sail/ui';
import {arrowUp, paperclip} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';
import {InputProps} from './InputProps';
import {useAnalytics} from '../AnalyticsProvider';
import {useUniversalHelpEncourageMoreDetailFromUserMse5042Enabled} from '../FeatureFlagsProvider';
import {useTheme} from '../ThemeProvider';
import {Theme} from '../theme';

const messages = defineMessages({
  sendMessageAriaLabel: {
    id: 'conversations_ui.chat.input.sendButton.ariaLabel',
    description: 'Label for the chat textbox send button',
    defaultMessage: 'Send message',
  },
  selectAttachmentAriaLabel: {
    id: 'conversations_ui.chat.input.selectAttachment.ariaLabel',
    description: 'Label for the chat textbox select attachment',
    defaultMessage: 'Select attachment',
  },
});

const HiddenFileInput = ({
  onChange,
  fileInputRef,
}: {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: RefObject<HTMLInputElement>;
}): JSX.Element => (
  <view.input
    data-testid="file-input"
    type="file"
    value=""
    onChange={onChange}
    css={{display: 'none'}}
    ref={fileInputRef}
  />
);

function useIsOverflowing(
  textareaRef: RefObject<HTMLTextAreaElement>,
  dependencies: unknown[],
): boolean {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textareaRef.current) {
        setIsOverflowing(
          textareaRef.current.scrollHeight > textareaRef.current.clientHeight,
        );
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return isOverflowing;
}

export const AttachmentButton = ({
  onSelect,
  handleFileInputKeyDown,
  fileInputRef,
}: {
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileInputKeyDown: (e: React.KeyboardEvent) => void;
  fileInputRef: RefObject<HTMLInputElement>;
}): JSX.Element => {
  const intl = useIntl();
  const isEncourageMoreDetailFlagEnabled =
    useUniversalHelpEncourageMoreDetailFromUserMse5042Enabled();

  return (
    <view.label
      aria-label={intl.formatMessage(messages.selectAttachmentAriaLabel)}
      css={{
        border: 'none',
        cursor: 'pointer',
        maxWidth: 'medium',
        marginTop: 'xsmall',
        marginRight: 'small',
      }}
      uses={[
        isEncourageMoreDetailFlagEnabled
          ? css({
              position: 'absolute',
              right: '40px',
              bottom: '8px',
              marginTop: 0,
              marginRight: 0,
              zIndex: 2,
            })
          : null,
      ]}
      tabIndex={0}
      onKeyDown={handleFileInputKeyDown}
    >
      <Icon
        icon={paperclip}
        data-testid="attachment-icon"
        css={{color: 'action.primary.disabled', ':hover': {color: 'subdued'}}}
      />
      <HiddenFileInput onChange={onSelect} fileInputRef={fileInputRef} />
    </view.label>
  );
};

export function InputNext({
  disabled,
  hidden,
  placeholder,
  attachmentDisabled,
  sendMessageDisabled,
  onSendMessage,
  onSendTyping,
  onSendFile,
  onInputFocus,
  onInputBlur,
  onInputChanged,
}: InputProps) {
  const isEncourageMoreDetailFlagEnabled =
    useUniversalHelpEncourageMoreDetailFromUserMse5042Enabled();
  const analytics = useAnalytics();
  const theme = useTheme();
  // In this implementation, hiding is the same as disabling
  const [enteredText, setEnteredText] = useState('');
  const isDisabled = disabled || hidden;
  const isSendDisabled =
    isDisabled || sendMessageDisabled || enteredText === '';
  const showAttachmentButton =
    !!onSendFile && !attachmentDisabled && !enteredText && !isDisabled;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isOverflowing = useIsOverflowing(textareaRef, [enteredText]);

  const sendMessage = () => {
    if (!sendMessageDisabled) {
      const messageBody = enteredText.trim();
      setEnteredText('');

      if (messageBody) {
        onSendMessage(messageBody);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // Note: We are intentionally using a deprecated property, keyCode, in support for IME(Input Method Editor) Enters.
    // Why: For some languages like Japanese, IME is used to transform qwerty inputs into characters, and the user needs to hit Enter to select.
    // However, we are also looking to send the user message on Enter. So we need a way to differentiate a normal Enter vs IME Enter.
    // In the deprecated properties like 'keyCode' and 'which', the two Enters are represented with codes '13' (normal) and '229' (IME).
    // In nondeprecated properties like 'key' and 'code', both are represented as 'Enter'.
    // Since keyCode is still supported in majority of the browsers today, and the legacy Copilot input used keyCode for the longest time, we'll continue to use keyCode here for now.
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();

      sendMessage();
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    const newEnteredText = event.target.value;
    setEnteredText(newEnteredText);
    onInputChanged?.(newEnteredText);
    if (newEnteredText.length) {
      onSendTyping?.();
    }
  };

  const handleFileSelection = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    analytics.action('file_attachment_sent');
    e.target.files && onSendFile && onSendFile(e.target.files[0]);
  };

  const handleFileInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  return (
    <view.div
      css={{
        paddingX: 'medium',
        paddingBottom: 'medium',
        flexGrow: 0,
        flexShrink: 0,
        zIndex: 1,
      }}
    >
      <view.div
        data-testid="text-input-container"
        css={{
          keyline: tokens.color.border.toString(),
          borderRadius: isEncourageMoreDetailFlagEnabled ? '12px' : '24px',
          overflow: 'hidden',
          boxShadow: isDisabled ? 'none' : 'overlay.inverted',
          backgroundColor: isDisabled ? tokens.color.neutral[50] : 'surface',
          width: 'fill',
          position: 'relative',
          stack: 'x',
          alignY: 'center',
          transition: '.5s background-color, .5s box-shadow',
          ':focus-within': {
            keyline: theme.primaryActionColor,
            keylineWidth: 'small',
          },
        }}
      >
        <view.textarea
          ref={textareaRef}
          key="textarea"
          data-testid="text-input"
          autoFocus
          disabled={isDisabled}
          placeholder={placeholder}
          value={enteredText || ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          css={{
            appearance: 'none',
            border: 'none',
            lineHeight: 'label.large',
            paddingY: 'small',
            paddingLeft: 'medium',
            backgroundColor: 'transparent',
            '::placeholder': {
              color: 'form.disabled',
            },
            fieldSizing: 'content',
            resize: 'none',
            ':focus': {outline: 'none'},
          }}
          uses={[
            isEncourageMoreDetailFlagEnabled
              ? css({
                  maxHeight: '8rlh',
                  minHeight: 88,
                  paddingRight: isOverflowing ? '64px' : '48px',
                })
              : css({
                  maxHeight: '10rlh',
                  minHeight: '40px',
                  paddingRight: 'space.75',
                }),
          ]}
        />
        {showAttachmentButton ? (
          <AttachmentButton
            onSelect={handleFileSelection}
            handleFileInputKeyDown={handleFileInputKeyDown}
            fileInputRef={fileInputRef}
          />
        ) : null}
        <SendMessageButton
          isSendDisabled={isSendDisabled}
          sendMessage={sendMessage}
          setEnteredText={setEnteredText}
          theme={theme}
        />
      </view.div>
    </view.div>
  );
}

function SendMessageButton({
  isSendDisabled,
  sendMessage,
  setEnteredText,
  theme,
}: {
  isSendDisabled: boolean;
  sendMessage: () => void;
  setEnteredText: (text: string) => void;
  theme: Theme;
}) {
  const isEncourageMoreDetailFlagEnabled =
    useUniversalHelpEncourageMoreDetailFromUserMse5042Enabled();
  return (
    <Button
      onPress={() => {
        sendMessage();
        setEnteredText('');
      }}
      key="button"
      size="small"
      disabled={isSendDisabled}
      data-testid="send-button"
      css={{
        borderRadius: 'rounded',
        aspectRatio: '1 / 1',
        '::after': {
          borderRadius: 'rounded',
        },
      }}
      uses={[
        isEncourageMoreDetailFlagEnabled
          ? css({
              position: 'absolute',
              right: '8px',
              bottom: '8px',
              zIndex: 2,
              marginRight: 0,
            })
          : css({
              marginRight: 'space.75',
            }),
        isSendDisabled
          ? css({
              backgroundColor: tokens.color.neutral[50],
              boxShadow: 'none',
              color: 'form.disabled',
              '::after': {
                display: 'none',
              },
            })
          : css({
              backgroundColor: theme.primaryActionColor,
              color: 'white',
              borderColor: theme.primaryActionColor,
              ':hover:not(:active):not([aria-disabled="true"])': {
                backgroundColor: theme.primaryActionColor,
                keyline: theme.ctaHoverColor,
                topShadow: theme.primaryActionColor,
                color: 'white',
              },
              ':active:not([aria-disabled="true"])': {
                topShadow: theme.pressedShadow,
                backgroundColor: theme.primaryActionColor,
                keyline: theme.primaryActionColor,
                color: 'white',
              },
            }),
      ]}
    >
      <Icon icon={arrowUp} size="xsmall" />
    </Button>
  );
}
